import {
  Owner,
  ContentType,
  CollectionEpic,
  StoryRepo,
  StoryAttachment,
  Collection,
  UserType,
} from '@amzn/awscat-pattern-library-service-model';

import { reorderById } from '../../common/Arrays';
import constants from '../../common/constants';
import CollectionEpicWrapper from './CollectionEpicWrapper';
import CollectionStoryWithIndex from './CollectionStoryWithIndex';
import { EDIT_COLLECTION_CHAR_LIMIT } from '../../api/UtilsApi';

export interface CollectionEpicCsv extends CollectionEpic {
  epicOrder: number;
  storyOrder: number;
  storyTitle: string;
  storyDescription: string;
  publicationName: string;
  // @ts-expect-error TS7008 - strict migration
  publicationId;
  contentType: ContentType;
  repos: StoryRepo[];
  attachments: StoryAttachment[];
}

export const MAX_TITLE_LENGTH = 500;
export const MAX_DESCRIPTION_LENGTH = 500;

export const DOWNLOAD_CSV_BUTTON_TEXT = 'CSV';

export const displayOwners = (owners: Owner[]) => {
  let ownersInfo = '';
  owners.forEach((owner, index) => {
    const { name, userType, organizationName } = owner;
    const organization = userType === UserType.AWS ? 'AWS' : (organizationName ?? '');
    const organizationText = organization ? ` (${organization})` : '';
    if (index === 0) {
      ownersInfo += `${name}${organizationText}`;
    } else if (index === owners.length - 1) {
      ownersInfo += ` and ${name}${organizationText}`;
    } else {
      ownersInfo += `, ${name}${organizationText}`;
    }
  });
  return ownersInfo;
};

export const generateEpicCsvData = (epics: CollectionEpic[]) => {
  const collecitonEpicCsvData: CollectionEpicCsv[] = [];
  epics.forEach((epic, epicIndex) => {
    const { stories } = epic;
    stories.forEach((story, storyIndex) => {
      const epicCsvItem = {
        ...epic,
        epicOrder: epicIndex + 1,
        storyOrder: storyIndex + 1,
        storyTitle: story.title,
        storyDescription: story.description,
        publicationName: story.publicationName,
        publicationId: story.publicationId,
        contentType: story.contentType,
        repos: story.repos,
        attachments: story.attachments,
      };
      // @ts-expect-error TS2345 - strict migration
      collecitonEpicCsvData.push(epicCsvItem);
    });
  });
  return collecitonEpicCsvData;
};

export const CollectionEpicCsvFormat: Array<{
  header: string;
  value: (e: CollectionEpicCsv) => any;
}> = [
  {
    header: constants.COLLECTION_EPIC_CSV_HEADER_EPIC_ORDER,
    value: (i) => i.epicOrder,
  },
  {
    header: constants.COLLECTION_EPIC_CSV_HEADER_EPIC_TITLE,
    value: (i) => i.title,
  },
  {
    header: constants.COLLECTION_EPIC_CSV_HEADER_EPIC_DESC,
    value: (i) => i.description,
  },
  {
    header: constants.COLLECTION_EPIC_CSV_HEADER_STORY_ORDER,
    value: (i) => i.storyOrder,
  },
  {
    header: constants.COLLECTION_EPIC_CSV_HEADER_STORY_TITLE,
    value: (i) => i.storyTitle,
  },
  {
    header: constants.COLLECTION_EPIC_CSV_HEADER_STORY_DESC,
    value: (i) => i.storyDescription,
  },
  {
    header: constants.COLLECTION_VIEWER_GUIDANCE_COL,
    value: (i) => `${i.contentType}: ${i.publicationName}`,
  },
  {
    header: constants.COLLECTION_VIEWER_ATTACHMENT_AND_REPO_COL,
    value: (i) => getStoryReposAndAttachment(i.repos, i.attachments),
  },
];

const getStoryReposAndAttachment = (repos: StoryRepo[], attachments: StoryAttachment[]) => {
  let reposAndAttachmentCsv = '';
  if (repos?.length) {
    reposAndAttachmentCsv += `${constants.COLLECTION_VIEWER_REPOSITORY}: \n`;
  }
  repos?.forEach((repo) => {
    reposAndAttachmentCsv += `${repo.name} (${repo.link})\n`;
  });
  if (attachments?.length) {
    reposAndAttachmentCsv += `\n${constants.COLLECTION_VIEWER_ATTACHMENTS}: \n`;
  }
  attachments?.forEach((attachment) => {
    reposAndAttachmentCsv += `${attachment.name} (${attachment.link})\n`;
  });
  return reposAndAttachmentCsv;
};

/**
 * Builds the backend epics payload from the editor wrappers; use before saving or publishing so
 * client-only fields (ids, positions, search state) never reach the service.
 */
export const formatEpics = (epics: readonly CollectionEpicWrapper[]) => {
  return epics.map((epic) => ({
    title: epic.title,
    description: epic.description,
    stories: epic.stories.map((story) => story.story),
  }));
};

/** Returns the epics ordered to match `newOrderIds`; use after an epic drag-and-drop. */
export function reorderCollectionEpics(
  epics: readonly CollectionEpicWrapper[],
  newOrderIds: readonly string[],
): CollectionEpicWrapper[] {
  return reorderById(epics, newOrderIds);
}

/**
 * Returns the epics with one epic's stories ordered to match `newOrderIds` and every story's
 * `index` renumbered to its new position; use after a story drag-and-drop.
 */
export function reorderCollectionStories(
  epics: readonly CollectionEpicWrapper[],
  epicId: string,
  newOrderIds: readonly string[],
): CollectionEpicWrapper[] {
  return epics.map((epic) => {
    // Other epics keep their identity so their lists don't re-render.
    if (epic.id !== epicId) return epic;

    // Rebuild every story so `index` (used for lookups) matches its new position.
    const stories = reorderById(epic.stories, newOrderIds).map(
      (story, index) =>
        new CollectionStoryWithIndex(
          story.story,
          index,
          story.url,
          story.searchTerm,
          story.addedPublication,
          story.id,
        ),
    );
    return new CollectionEpicWrapper(epic.title, epic.description, stories, epic.id);
  });
}

export const validateCollection = (collection: Collection) => {
  let errors: string[] = [];
  const { title, description } = collection;
  let hasError = false;
  if (!title) {
    errors.push('Collection title is required.');
  }
  if (!description) {
    errors.push('Collection description is required.');
  }
  if (title.length > EDIT_COLLECTION_CHAR_LIMIT) {
    errors.push('Collection title must not be over ' + EDIT_COLLECTION_CHAR_LIMIT + ' characters.');
  }
  if (description.length > EDIT_COLLECTION_CHAR_LIMIT) {
    errors.push(
      'Collection description must not be over ' + EDIT_COLLECTION_CHAR_LIMIT + ' characters.',
    );
  }
  errors = validateEpics(collection, errors);
  if (errors.length) {
    hasError = true;
  }
  return {
    errors,
    hasError,
  };
};

const validateEpics = (collection: Collection, errors: string[]) => {
  const { epics } = collection;
  let missingTitle = false;
  let misingDescription = false;
  let overCharLimitTitle = false;
  let overCharLimitDescr = false;

  // @ts-expect-error TS18048 - strict migration
  epics.forEach((epic) => {
    const { title, description } = epic;
    if (!title) {
      missingTitle = true;
    }
    if (!description) {
      misingDescription = true;
    }
    // @ts-expect-error TS18048 - strict migration
    if (title.length > EDIT_COLLECTION_CHAR_LIMIT) {
      overCharLimitTitle = true;
    }
    // @ts-expect-error TS18048 - strict migration
    if (description.length > EDIT_COLLECTION_CHAR_LIMIT) {
      overCharLimitDescr = true;
    }
  });
  if (missingTitle) {
    errors.push('Epic title is required.');
  }
  if (misingDescription) {
    errors.push('Epic description is required.');
  }
  if (overCharLimitTitle) {
    errors.push('Epic title must not be over ' + EDIT_COLLECTION_CHAR_LIMIT + ' characters.');
  }
  if (overCharLimitDescr) {
    errors.push('Epic description must not be over ' + EDIT_COLLECTION_CHAR_LIMIT + ' characters.');
  }
  // @ts-expect-error TS2345 - strict migration
  errors = validateStories(epics, errors);
  return errors;
};

const validateStories = (epics: CollectionEpic[], errors: string[]) => {
  let missingTitle = false;
  let missingDescription = false;
  let missingPublication = false;
  let overCharLimitStoryTitle = false;
  let overCharLimitStoryDescr = false;

  epics.forEach((epic) => {
    const { stories } = epic;
    stories.forEach((story) => {
      const { title, description, publicationId } = story;
      if (!title) {
        missingTitle = true;
      }
      if (!description) {
        missingDescription = true;
      }
      if (!publicationId) {
        missingPublication = true;
      }
      // @ts-expect-error TS18048 - strict migration
      if (title.length > EDIT_COLLECTION_CHAR_LIMIT) {
        overCharLimitStoryTitle = true;
      }
      // @ts-expect-error TS18048 - strict migration
      if (description.length > EDIT_COLLECTION_CHAR_LIMIT) {
        overCharLimitStoryDescr = true;
      }
    });
  });
  if (missingTitle) {
    errors.push('Title is required for an epic story.');
  }
  if (missingDescription) {
    errors.push('Description is required for an epic story.');
  }
  if (missingPublication) {
    errors.push('Guidance is required for an epic story.');
  }
  if (overCharLimitStoryTitle) {
    errors.push('Story title must not be over ' + EDIT_COLLECTION_CHAR_LIMIT + ' characters.');
  }
  if (overCharLimitStoryDescr) {
    errors.push(
      'Story description must not be over ' + EDIT_COLLECTION_CHAR_LIMIT + ' characters.',
    );
  }
  return errors;
};

// validate collection title and description when creating collection
export const validateCollectionCreation = (title: string, description: string) => {
  const errors: string[] = [];
  let hasError = false;
  if (!title) {
    errors.push('Title is required for creating collection.');
    hasError = true;
  }
  if (!description) {
    errors.push('Description is required for creating collection.');
    hasError = true;
  }
  return {
    hasError,
    errors,
  };
};
